/**
 * TransformationModes — Section "3 façons de te transformer" en Home.
 *
 * Communique instantanément les 3 formats disponibles avec CTA directs :
 *   1. En studio à Nyon           → BOOKING_URL (Pure-informatique, 30min gratuit)
 *   2. 100% en ligne              → /postuler-coaching?type=online
 *   3. VIP Hybride                → /postuler-coaching?type=hybride
 * + une 4e carte "L'App Body-Up" (design différencié) qui détaille les fonctionnalités
 * de l'application, incluses selon la formule choisie → /application
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Dumbbell, Monitor, ArrowRight, Calendar, Sparkles, MapPin, Crown, Smartphone,
  Flame, ChefHat, TrendingUp, Video, Weight, Activity, Trophy, Bot, Salad,
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { BOOKING_URL } from '../data/constants';

const APP_PHONE_IMAGE = 'https://static.prod-images.emergentagent.com/jobs/7ec0a196-30d6-48cc-b4da-a55574a12eb3/images/21d9ac22862f813438c352e87833884fb7cb21b315c726375bd19b4f05826e46.jpeg';

const APP_FEATURES = [
  { icon: Flame, fr: 'Calculateur de calories ultra précis (suivi complet, spécial femmes en périménopause/ménopause)', en: 'Ultra-precise calorie calculator (full tracking, special support for perimenopause/menopause)' },
  { icon: ChefHat, fr: 'Plus de 150 recettes hyper protéinées', en: '150+ high-protein recipes' },
  { icon: TrendingUp, fr: 'Suivi de progression constante (nutrition + programme sportif)', en: 'Constant progress tracking (nutrition + training program)' },
  { icon: Dumbbell, fr: 'Programmes de musculation personnalisés', en: 'Personalized strength-training programs' },
  { icon: Video, fr: 'Bibliothèque d\'exercices en vidéo avec explications ultra précises', en: 'Video exercise library with ultra-precise instructions' },
  { icon: Weight, fr: 'Objectifs et tracking du tonnage (poids soulevé par séance/exercice)', en: 'Goals & tonnage tracking (weight lifted per session/exercise)' },
  { icon: Activity, fr: 'Suivi de tes performances (évolution de ta force)', en: 'Performance tracking (your strength progress)' },
  { icon: Trophy, fr: 'Calcul de tes RM (répétitions maximales) par exercice', en: 'Your 1RM (max reps) calculated per exercise' },
  { icon: Bot, fr: 'Programme créé avec ton Coach IA ou un coach certifié Body-Up', en: 'Program built with your AI Coach or a certified Body-Up coach' },
  { icon: Salad, fr: 'Plan alimentaire personnalisé par IA ou coach certifié', en: 'Meal plan personalized by AI or a certified coach' },
];

const MODES = [
  {
    key: 'presentiel',
    icon: Dumbbell,
    badge: ['Le vrai luxe', 'Real luxury'],
    accentClass: 'from-amber-500 to-orange-500',
    accentBorder: 'border-amber-500/40',
    accentGlow: 'shadow-amber-500/20',
    accentText: 'text-amber-400',
    title: ['En studio à Nyon', 'In studio in Nyon'],
    subtitle: ['Coaching présentiel', 'In-person coaching'],
    description: [
      'Sessions 1-to-1 dans le studio privé de 150m². EMS Drysuit, scanner 3D Styku, matériel pro. Laurent te coache face à face.',
      'One-on-one sessions in the 150m² private studio. EMS Drysuit, 3D Styku scanner, pro equipment. Laurent coaches you face to face.',
    ],
    price: ['dès 65 CHF/session', 'from 65 CHF/session'],
    features: [
      ['Studio privé exclusif à Nyon', 'Exclusive private studio in Nyon'],
      ['Équipement professionnel', 'Pro-grade equipment'],
      ['20+ ans d\'expérience', '20+ years of experience'],
    ],
    ctaLabel: ['Réserver mon 1er RDV gratuit', 'Book my free 1st session'],
    ctaSub: ['30 min · Studio Body-Up Nyon', '30 min · Body-Up Studio Nyon'],
    isExternal: true,
    href: BOOKING_URL,
    testId: 'mode-presentiel',
  },
  {
    key: 'online',
    icon: Monitor,
    badge: ['Nouveauté 2026', '2026 launch'],
    accentClass: 'from-primary to-pink-500',
    accentBorder: 'border-primary/40',
    accentGlow: 'shadow-primary/25',
    accentText: 'text-primary',
    title: ['100% en ligne', '100% online'],
    subtitle: ['Coaching à distance', 'Remote coaching'],
    description: [
      'Programme personnalisé, 6 visios Zoom incluses avec Laurent, messagerie 24/7. Transforme ton corps depuis chez toi, partout dans le monde.',
      'Personalized program, 6 included Zoom calls with Laurent, 24/7 messaging. Transform your body from home, anywhere in the world.',
    ],
    price: ['299 CHF/mois · 6 mois', '299 CHF/month · 6 months'],
    features: [
      ['Programme sur-mesure chaque mois', 'Custom program every month'],
      ['6 visios Zoom 30 min incluses (≈1/mois)', '6 included 30-min Zoom calls (≈1/month)'],
      ['Messagerie 24/7 + ajustements', '24/7 messaging + adjustments'],
    ],
    ctaLabel: ['Postuler pour un appel', 'Apply for a call'],
    ctaSub: ['30 min Zoom gratuit · sur candidature', 'Free 30 min Zoom · by application'],
    isExternal: false,
    href: '/postuler-coaching?type=online',
    testId: 'mode-online',
  },
  {
    key: 'hybride',
    icon: Crown,
    badge: ['Premium · Suisse Romande', 'Premium · Swiss Romande'],
    accentClass: 'from-purple-500 to-primary',
    accentBorder: 'border-purple-500/40',
    accentGlow: 'shadow-purple-500/25',
    accentText: 'text-purple-400',
    title: ['VIP Hybride', 'VIP Hybrid'],
    subtitle: ['En ligne + présentiel', 'Online + in-person'],
    description: [
      'Le meilleur des deux mondes : le suivi en ligne complet + 6 séances de 60 min au choix (studio Nyon ou Zoom). Pour ceux qui veulent le premium.',
      'Best of both worlds: full online tracking + 6× 60-minute sessions of your choice (Nyon studio or Zoom). For those who want premium.',
    ],
    price: ['399 CHF/mois · 6 mois', '399 CHF/month · 6 months'],
    features: [
      ['Tout du Pack Transformation (hors les 6 visios Zoom)', 'Everything in Transformation Pack (except the 6 Zoom calls)'],
      ['6 séances 60\' au choix : studio Nyon OU Zoom', '6× 60\' sessions, your choice: Nyon studio OR Zoom'],
      ['2 scans 3D complets (début + fin)', '2 full 3D scans (start + end)'],
    ],
    ctaLabel: ['Postuler pour un appel', 'Apply for a call'],
    ctaSub: ['30 min Zoom gratuit · sur candidature', 'Free 30 min Zoom · by application'],
    isExternal: false,
    href: '/postuler-coaching?type=hybride',
    testId: 'mode-hybride',
  },
];

export const TransformationModes = () => {
  const { language } = useLanguage();
  const en = language === 'en';
  const L = (fr, eng) => (en ? eng : fr);
  const [recipesCount, setRecipesCount] = React.useState(null);
  React.useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/recipes/count`).then((r) => r.json()).then((d) => setRecipesCount(d.count)).catch(() => {});
  }, []);

  return (
    <section className="relative py-16 sm:py-20 bg-black overflow-hidden" data-testid="transformation-modes">
      {/* Ambient glow */}
      <div className="pointer-events-none absolute inset-0" aria-hidden="true">
        <div className="absolute top-0 left-1/4 w-[24rem] h-[24rem] bg-primary/15 rounded-full blur-[120px]" />
        <div className="absolute bottom-0 right-1/4 w-[22rem] h-[22rem] bg-purple-500/10 rounded-full blur-[120px]" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <div className="text-center mb-10 sm:mb-14">
          <span className="inline-block px-3 py-1 rounded-full text-[10px] font-bold tracking-widest bg-white/5 border border-white/10 text-zinc-300 mb-4">
            {L('BODY-UP · 4 FAÇONS DE TE COACHER', 'BODY-UP · 4 WAYS TO COACH YOU')}
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white leading-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {L('Choisis ton format,', 'Pick your format,')}{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary via-pink-500 to-purple-400">
              {L('je m\'adapte à toi', 'I adapt to you')}
            </span>
          </h2>
          <p className="text-zinc-400 mt-4 max-w-2xl mx-auto text-sm sm:text-base leading-relaxed">
            {L(
              'Que tu habites à Nyon ou à l\'autre bout du monde, il y a un programme Body-Up taillé pour toi. Un seul objectif : ta transformation.',
              'Whether you live in Nyon or on the other side of the world, there\'s a Body-Up program built for you. One goal: your transformation.'
            )}
          </p>
        </div>

        {/* 3 cards */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 sm:gap-6">
          {MODES.map((m, i) => {
            const Icon = m.icon;
            const cardBody = (
              <div className={`relative h-full flex flex-col overflow-hidden rounded-3xl border-2 ${m.accentBorder} bg-gradient-to-br from-zinc-900 via-zinc-950 to-black p-6 sm:p-7 shadow-2xl ${m.accentGlow} hover:scale-[1.02] transition-transform duration-300`}>
                {/* Corner glow */}
                <div className={`absolute -top-16 -right-16 w-40 h-40 rounded-full bg-gradient-to-br ${m.accentClass} opacity-20 blur-3xl`} aria-hidden="true" />

                {/* Badge */}
                <div className="flex items-center justify-between mb-4">
                  <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold tracking-wider bg-white/5 border ${m.accentBorder} ${m.accentText}`}>
                    {m.key === 'presentiel' && <MapPin className="w-3 h-3" />}
                    {m.key === 'online' && <Sparkles className="w-3 h-3" />}
                    {m.key === 'hybride' && <Crown className="w-3 h-3" />}
                    {m.badge[en ? 1 : 0]}
                  </span>
                  <div className={`w-11 h-11 rounded-2xl bg-gradient-to-br ${m.accentClass} flex items-center justify-center shadow-lg ${m.accentGlow}`}>
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                </div>

                {/* Title */}
                <h3 className="text-2xl sm:text-3xl font-black text-white leading-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {m.title[en ? 1 : 0]}
                </h3>
                <p className={`text-xs sm:text-sm font-bold uppercase tracking-wider mt-1 ${m.accentText}`}>
                  {m.subtitle[en ? 1 : 0]}
                </p>

                {/* Price */}
                <div className={`inline-block mt-3 px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 text-xs font-bold ${m.accentText} self-start`}>
                  {m.price[en ? 1 : 0]}
                </div>

                {/* Description */}
                <p className="text-zinc-400 text-sm mt-4 leading-relaxed">
                  {m.description[en ? 1 : 0]}
                </p>

                {/* Features */}
                <ul className="mt-5 space-y-2 flex-1">
                  {m.features.map((f) => (
                    <li key={f[0]} className="flex items-start gap-2 text-zinc-300 text-sm">
                      <span className={`w-1.5 h-1.5 rounded-full bg-gradient-to-r ${m.accentClass} mt-1.5 shrink-0`} />
                      {f[en ? 1 : 0]}
                    </li>
                  ))}
                </ul>

                {/* CTA */}
                <div className="mt-6 pt-5 border-t border-white/5">
                  <div className={`w-full inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-xl bg-gradient-to-r ${m.accentClass} text-white text-sm font-bold shadow-lg ${m.accentGlow} group-hover:scale-[1.02] transition-transform`}>
                    {m.key === 'presentiel' ? <Calendar className="w-4 h-4" /> : <Sparkles className="w-4 h-4" />}
                    <span>{m.ctaLabel[en ? 1 : 0]}</span>
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </div>
                  <p className="text-[11px] text-zinc-500 text-center mt-2">
                    {m.ctaSub[en ? 1 : 0]}
                  </p>
                </div>
              </div>
            );

            return (
              <motion.div
                key={m.key}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-80px' }}
                transition={{ delay: i * 0.12, duration: 0.5, ease: 'easeOut' }}
                className="group"
                data-testid={m.testId}
              >
                {m.isExternal ? (
                  <a href={m.href} target="_blank" rel="noopener noreferrer" className="block h-full">
                    {cardBody}
                  </a>
                ) : (
                  <Link to={m.href} className="block h-full">
                    {cardBody}
                  </Link>
                )}
              </motion.div>
            );
          })}
        </div>

        {/* 4e carte — L'App Body-Up (design différencié : cyan/emerald + visuel téléphone 3D) */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ delay: 0.36, duration: 0.5, ease: 'easeOut' }}
          className="mt-5 sm:mt-6"
          data-testid="mode-app"
        >
          <div className="relative overflow-hidden rounded-3xl border-2 border-cyan-500/40 bg-gradient-to-br from-zinc-900 via-zinc-950 to-black shadow-2xl shadow-cyan-500/20">
            <div className="absolute -top-24 -left-24 w-72 h-72 rounded-full bg-gradient-to-br from-cyan-400 to-emerald-500 opacity-20 blur-3xl" aria-hidden="true" />
            <div className="absolute -bottom-24 -right-24 w-72 h-72 rounded-full bg-gradient-to-br from-emerald-400 to-cyan-500 opacity-15 blur-3xl" aria-hidden="true" />

            <div className="relative grid lg:grid-cols-[1fr_1.4fr] gap-8 lg:gap-4 p-6 sm:p-8 lg:p-10 items-center">
              {/* Visuel téléphone 3D */}
              <div className="flex justify-center lg:justify-start order-1 lg:order-none">
                <img
                  src={APP_PHONE_IMAGE}
                  alt="L'application Body-Up"
                  loading="lazy"
                  width="320"
                  height="478"
                  style={{
                    WebkitMaskImage: 'radial-gradient(ellipse 58% 62% at 50% 48%, black 50%, transparent 82%)',
                    maskImage: 'radial-gradient(ellipse 58% 62% at 50% 48%, black 50%, transparent 82%)',
                  }}
                  className="w-56 sm:w-72 lg:w-80 drop-shadow-[0_0_60px_rgba(34,211,238,0.4)]"
                  data-testid="app-mode-phone-image"
                />
              </div>

              {/* Texte + features */}
              <div>
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold tracking-wider bg-white/5 border border-cyan-500/40 text-cyan-300">
                  <Smartphone className="w-3 h-3" /> {L('L\'App Body-Up', 'The Body-Up App')}
                </span>

                <h3 className="mt-3 text-2xl sm:text-3xl lg:text-[2rem] font-black text-white leading-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {L('Une app pensée par un expert en coaching depuis plus de 20 ans', 'An app designed by a coaching expert with 20+ years of experience')}
                </h3>

                <ul className="mt-6 grid sm:grid-cols-2 gap-2.5" data-testid="app-mode-features">
                  {APP_FEATURES.map((f, i) => {
                    const FIcon = f.icon;
                    return (
                      <li key={i} className="flex items-start gap-2 text-zinc-300 text-xs sm:text-[13px] leading-snug" data-testid={`app-mode-feature-${i}`}>
                        <FIcon className="w-3.5 h-3.5 text-cyan-400 mt-0.5 shrink-0" />
                        <span>{i === 1 && recipesCount ? L(`Plus de ${recipesCount} recettes hyper protéinées`, `${recipesCount}+ high-protein recipes`) : f[en ? 'en' : 'fr']}</span>
                      </li>
                    );
                  })}
                </ul>

                <p className="mt-5 text-[11px] sm:text-xs text-zinc-500 italic">
                  {L('* Fonctionnalités incluses selon la formule d\'abonnement choisie.', '* Features included depend on the subscription plan chosen.')}
                </p>

                <Link
                  to="/application"
                  className="mt-5 inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-gradient-to-r from-cyan-500 to-emerald-500 text-white text-sm font-bold shadow-lg shadow-cyan-500/25 hover:scale-[1.02] active:scale-[0.98] transition-transform"
                  data-testid="app-mode-cta"
                >
                  <Sparkles className="w-4 h-4" />
                  {L('Découvrir l\'App Body-Up', 'Discover the Body-Up App')}
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Bottom reassurance */}
        <p className="text-center text-zinc-500 text-xs sm:text-sm mt-8 max-w-xl mx-auto">
          {L(
            '🇨🇭 Coach certifié depuis 2005 · Consultation gratuite pour trouver le format qui te convient',
            '🇨🇭 Certified coach since 2005 · Free consultation to find the format that suits you'
          )}
        </p>
      </div>
    </section>
  );
};
